import { DEV } from 'cc/env';
import { _decorator, v4, VideoClip, Enum, Component, JsonAsset, Node, Sprite, SpriteFrame, error, warn } from 'cc';
import NodePoolInstance from '../../CCClass/NodePoolInstance';
import RemoteLoader from '../../Helper/Loading/RemoteLoader';
import Delay from '../../Utils/Delay';
import VideoSprite from '../VideoSprite/VideoSprite';
import { IVapData, VapLocalData, VapRemoteData, toUvArea } from './Data/VapData';
import VapVideoSprite, { ResourceType } from './VapVideoSprite';

const { ccclass, executeInEditMode, property } = _decorator;

@ccclass('VapAsset')
class VapAsset {
    @property({ displayName: DEV && 'Vap 名稱' })
    public name: string = '';

    @property({ type: Enum(ResourceType) })
    public resourceType: ResourceType = ResourceType.LOCAL;

    @property({
        type: VideoClip,
        visible(this: VapAsset) {
            return this.resourceType === ResourceType.LOCAL;
        },
    })
    public clip: VideoClip = null;

    @property({
        type: JsonAsset,
        visible(this: VapAsset) {
            return this.resourceType === ResourceType.LOCAL;
        },
    })
    public localJsonConfig: JsonAsset = null;

    @property({
        visible(this: VapAsset) {
            return this.resourceType === ResourceType.REMOTE;
        },
    })
    public remoteURL: string = '';

    @property({
        visible(this: VapAsset) {
            return this.resourceType === ResourceType.REMOTE;
        },
    })
    public remoteJsonConfigURL: string = '';

    public duration: number = 0;
}

interface IVapSourceInstance {
    vapName: string;
    node: Node;
    vapVideoSprite: VapVideoSprite;
    shared: boolean;
    // Context sprites reuse the source texture, so keep its VAP layout for their own material uniforms.
    vapData: IVapData;
    loadingPromise: Promise<SpriteFrame>;
}

@ccclass('VapVideoManager')
@executeInEditMode
export default class VapVideoManager extends Component {
    protected static instance: VapVideoManager = null;

    @property([VapAsset])
    public vapAssets: VapAsset[] = [];

    private _nonSharedSourcePool: NodePoolInstance = null;
    private _sourceTemplate: Node = null;
    private _sharedSources: Map<string, IVapSourceInstance> = new Map();
    private _ownerSourceMap: Map<Component, IVapSourceInstance> = new Map();

    public static get Instance() {
        return this.instance;
    }

    private get nonSharedSourcePool() {
        if (!this._nonSharedSourcePool) {
            this._nonSharedSourcePool = new NodePoolInstance();
            this._nonSharedSourcePool.root = this.node;
            this._nonSharedSourcePool.clonedNode = this.sourceTemplate;
            this._nonSharedSourcePool.init((node: Node) => {
                node.name = 'VapVideoSource';
                node.active = true;
            });
        }
        return this._nonSharedSourcePool;
    }

    private get sourceTemplate() {
        if (!this._sourceTemplate) {
            this._sourceTemplate = this._createSourceNode('VapVideoSourceTemplate');
            this._sourceTemplate.active = false;
        }
        return this._sourceTemplate;
    }

    public async getVapSpriteFrame(vapName: string, shared: boolean, owner: Component, isLoop: boolean = false, startTime: number = 0): Promise<SpriteFrame> {
        const vapAsset = this._getVapAsset(vapName);
        if (!vapAsset) {
            error(`VapVideoManager: Cannot find VapAsset "${vapName}".`);
            return null;
        }
        if (owner) {
            this._releaseOwnerNonSharedSource(owner);
        }

        const source = shared ? this._getSharedSource(vapAsset) : this._getNonSharedSource();
        let spriteFrame: SpriteFrame;
        if (shared && source.vapName === vapAsset.name && source.loadingPromise) {
            spriteFrame = await source.loadingPromise;
            if (spriteFrame) {
                spriteFrame = this._playExistingSource(source, isLoop, startTime);
            }
        } else {
            source.loadingPromise = this._updateAndPlaySource(source, vapAsset, isLoop, startTime);
            spriteFrame = await source.loadingPromise;
        }

        if (!spriteFrame) {
            if (!source.shared) {
                this._releaseNonSharedSource(source);
            }
            return null;
        }

        if (!source.vapData) {
            source.vapData = await this._loadVapData(vapAsset);
        }

        if (owner) {
            this._ownerSourceMap.set(owner, source);
        }
        vapAsset.duration = source.vapVideoSprite.videoElement.duration;
        return spriteFrame;
    }

    /**
     * Syncs the packed RGB/Alpha texture regions to a context Sprite.
     * The context Sprite must already have vap-sprite.mtl assigned by its scene or prefab.
     */
    public syncVapSprite(owner: Component) {
        const source = this._ownerSourceMap.get(owner);
        const targetSprite = owner?.getComponent(Sprite);
        if (!source?.vapData || !targetSprite) {
            return;
        }
        if (!targetSprite.customMaterial) {
            warn(`VapVideoManager: Sprite on node "${owner.node.name}" must use vap-sprite.mtl.`);
            return;
        }

        const { info } = source.vapData;
        const pass = targetSprite.material?.passes?.[0];
        if (!pass) {
            return;
        }

        // VAP packs RGB and Alpha into different rectangles; a normal Sprite material would show both.
        const alphaUv = toUvArea(info.aFrame, info.videoW, info.videoH);
        const rgbUv = toUvArea(info.rgbFrame, info.videoW, info.videoH);
        pass.setUniform(pass.getHandle('alphaFrameArea'), v4(...alphaUv));
        pass.setUniform(pass.getHandle('rgbFrameArea'), v4(...rgbUv));
        targetSprite.node.transform.setContentSize(info.w, info.h);
    }

    public getVapDuration(vapName: string): number {
        const vapAsset = this._getVapAsset(vapName);
        return vapAsset?.duration ?? 0;
    }

    public pauseVap(owner: Component) {
        const source = this._ownerSourceMap.get(owner);
        if (!source) {
            return;
        }
        // A shared source is still displayed by other owners, so only pause it when no one else uses it.
        if (!source.shared || !this._isSourceInUse(source, owner)) {
            source.vapVideoSprite.pause();
        }
    }

    public stopVap(owner: Component) {
        const source = this._ownerSourceMap.get(owner);
        if (!source) {
            return;
        }
        this._ownerSourceMap.delete(owner);
        if (!source.shared) {
            this._releaseNonSharedSource(source);
        } else if (!this._isSourceInUse(source)) {
            source.vapVideoSprite.stop();
        }
    }

    protected onLoad() {
        VapVideoManager.instance = this;
    }

    protected onDestroy() {
        if (VapVideoManager.instance === this) {
            VapVideoManager.instance = null;
        }
        this._sharedSources.forEach((source) => source.vapVideoSprite.stop());
        this._ownerSourceMap.forEach((source) => source.vapVideoSprite.stop());
        this._sharedSources.clear();
        this._ownerSourceMap.clear();
        if (this._nonSharedSourcePool) {
            this._nonSharedSourcePool.clear();
            this._nonSharedSourcePool = null;
        }
    }

    private _isSourceInUse(source: IVapSourceInstance, excludeOwner?: Component): boolean {
        for (const [owner, mappedSource] of this._ownerSourceMap) {
            if (owner !== excludeOwner && mappedSource === source) {
                return true;
            }
        }
        return false;
    }

    private _getSharedSource(vapAsset: VapAsset) {
        let source = this._sharedSources.get(vapAsset.name);
        if (!source) {
            source = this._createSourceInstance(this._createSourceNode(`VapVideoSource_${vapAsset.name}`), true);
            source.node.setParent(this.node);
            this._sharedSources.set(vapAsset.name, source);
        }
        return source;
    }

    private _getNonSharedSource(): IVapSourceInstance {
        const node = this.nonSharedSourcePool.get();
        node.active = true;
        return this._createSourceInstance(node, false);
    }

    private _releaseOwnerNonSharedSource(owner: Component) {
        const currentSource = this._ownerSourceMap.get(owner);
        if (!currentSource) {
            return;
        }
        if (!currentSource.shared) {
            this._releaseNonSharedSource(currentSource);
        }
        this._ownerSourceMap.delete(owner);
    }

    private _releaseNonSharedSource(source: IVapSourceInstance) {
        source.vapVideoSprite.stop();
        source.vapName = '';
        source.loadingPromise = null;
        source.node.active = false;
        this.nonSharedSourcePool.put(source.node);
    }

    private async _updateAndPlaySource(source: IVapSourceInstance, vapAsset: VapAsset, isLoop: boolean, startTime: number = 0): Promise<SpriteFrame> {
        source.vapName = vapAsset.name;
        source.vapData = null;
        const data = this._toVapData(vapAsset);
        const [firstFramePromise, cancelFirstFramePromise] = Delay.event(VideoSprite.EVENT_TYPE.UpdateFirstFrame, source.vapVideoSprite, undefined, true);
        await source.vapVideoSprite.updateSource(data);
        if (!source.vapVideoSprite.videoElement?.src) {
            error(`VapVideoManager: VapAsset "${vapAsset.name}" has no playable source.`);
            cancelFirstFramePromise();
            source.loadingPromise = null;
            return null;
        }
        source.vapVideoSprite.play(isLoop, 0);
        await firstFramePromise;
        if (startTime > 0) {
            source.vapVideoSprite.currentTime = startTime;
        }
        return source.vapVideoSprite.sprite.spriteFrame;
    }

    private _playExistingSource(source: IVapSourceInstance, isLoop: boolean, startTime: number = 0): SpriteFrame {
        if (!source.vapVideoSprite.isPlaying) {
            source.vapVideoSprite.play(isLoop, startTime);
        }
        return source.vapVideoSprite.sprite.spriteFrame;
    }

    private _createSourceNode(name: string) {
        const node = new Node(name);
        node.addComponent(Sprite);
        node.addComponent(VapVideoSprite);
        return node;
    }

    private _createSourceInstance(node: Node, shared: boolean): IVapSourceInstance {
        let vapVideoSprite = node.getComponent(VapVideoSprite);
        if (!vapVideoSprite) {
            vapVideoSprite = node.addComponent(VapVideoSprite);
        }
        if (!node.getComponent(Sprite)) {
            node.addComponent(Sprite);
        }
        return {
            vapName: '',
            node,
            vapVideoSprite,
            shared,
            vapData: null,
            loadingPromise: null,
        };
    }

    private async _loadVapData(vapAsset: VapAsset): Promise<IVapData> {
        if (vapAsset.resourceType === ResourceType.LOCAL) {
            return vapAsset.localJsonConfig?.json as IVapData;
        }
        if (!vapAsset.remoteJsonConfigURL) {
            return null;
        }
        const config = await RemoteLoader.loadJson(vapAsset.remoteJsonConfigURL);
        return config?.json as IVapData;
    }

    private _getVapAsset(vapName: string) {
        return this.vapAssets.find((vapAsset) => vapAsset.name && vapAsset.name === vapName);
    }

    private _toVapData(vapAsset: VapAsset): VapLocalData | VapRemoteData {
        if (vapAsset.resourceType === ResourceType.REMOTE) {
            return {
                name: vapAsset.name,
                videoUrl: vapAsset.remoteURL,
                configUrl: vapAsset.remoteJsonConfigURL,
            } as VapRemoteData;
        }
        return {
            clip: vapAsset.clip,
            jsonConfig: vapAsset.localJsonConfig,
        } as VapLocalData;
    }
}
